import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Button,
  CircularProgress,
  Tooltip,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  AddCircleOutline as AddCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CompareArrows as CompareIcon,
  ShieldOutlined as ShieldIcon,
  Fingerprint as FingerprintIcon,
  Search as SearchIcon,
  AssignmentTurnedIn as InitialIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import { userManagementUrl, createUsersClient } from "../../config";
import { GET_USERS } from "../../data/userData";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { getStoredAuditHistory } from "./auditLogger";

// ─── In-memory user cache ────────────────────────────────────────────────────
let cachedUsersMap = null;
let isFetchingUsers = false;
const userFetchListeners = [];

// ─── Fields to exclude from diffs ────────────────────────────────────────────
const SKIP_FIELDS = new Set([
  "id", "Id", "ID",
  "CreatedBy", "createdBy",
  "UpdatedBy", "updatedBy",
  "DeactevatedBy", "DeactivatedBy", "deactevatedBy", "deactivatedBy",
  "CreateDate", "CreatedDate", "createDate", "createdDate",
  "UpdateDate", "UpdatedDate", "updateDate", "updatedDate",
  "DeactivatedDate", "deactivatedDate",
  "__typename",
]);

const labelify = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

const formatAuditDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return String(dateStr); }
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays > 365) return `${Math.floor(diffDays / 365)}y ago`;
    if (diffDays > 30) return `${Math.floor(diffDays / 30)}mo ago`;
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return "just now";
  } catch { return ""; }
};

// ─── Timeline Item ────────────────────────────────────────────────────────────
function TimelineItem({
  type,
  icon,
  iconBg,
  iconShadow,
  title,
  titleColor,
  date,
  userName,
  relTime,
  diffItems = [],
  isDark,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyConfigured, setShowOnlyConfigured] = useState(false);

  const isCreatedEvent = type === "created";

  const configuredCount = useMemo(() => {
    return diffItems.filter(
      (d) => d.after !== "—" && d.after != null && String(d.after).trim() !== ""
    ).length;
  }, [diffItems]);

  const filteredItems = useMemo(() => {
    return diffItems.filter((d) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        d.field.toLowerCase().includes(q) ||
        String(d.after || "").toLowerCase().includes(q) ||
        String(d.before || "").toLowerCase().includes(q);

      if (!matchesSearch) return false;
      if (isCreatedEvent && showOnlyConfigured) {
        return d.after !== "—" && d.after != null && String(d.after).trim() !== "";
      }
      return true;
    });
  }, [diffItems, searchTerm, showOnlyConfigured, isCreatedEvent]);

  const renderBadge = (val, mode = "after") => {
    if (val === "—" || val == null || String(val).trim() === "") {
      return (
        <Typography
          variant="caption"
          sx={{
            color: isDark ? "#64748b" : "#94a3b8",
            fontStyle: "italic",
            fontSize: "0.74rem",
          }}
        >
          {isCreatedEvent ? "— Not configured —" : "—"}
        </Typography>
      );
    }

    const valStr = String(val);
    const isBoolTrue = valStr.toLowerCase() === "true";
    const isBoolFalse = valStr.toLowerCase() === "false";

    if (isBoolTrue) {
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            bgcolor: isDark ? "rgba(16,185,129,0.15)" : "#d1fae5",
            border: isDark ? "1px solid rgba(16,185,129,0.3)" : "1px solid #6ee7b7",
            color: isDark ? "#34d399" : "#047857",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            fontSize: "0.72rem",
            fontWeight: 700,
          }}
        >
          ● Active / True
        </Box>
      );
    }

    if (isBoolFalse) {
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            bgcolor: isDark ? "rgba(148,163,184,0.1)" : "#f1f5f9",
            border: isDark ? "1px solid rgba(148,163,184,0.2)" : "1px solid #cbd5e1",
            color: isDark ? "#94a3b8" : "#64748b",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            fontSize: "0.72rem",
            fontWeight: 700,
          }}
        >
          ○ Inactive / False
        </Box>
      );
    }

    const isAfter = mode === "after";
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          bgcolor: isAfter
            ? isDark ? "rgba(16,185,129,0.1)" : "#ecfdf5"
            : isDark ? "rgba(244,63,94,0.1)" : "#fff1f2",
          border: isAfter
            ? isDark ? "1px solid rgba(16,185,129,0.25)" : "1px solid #a7f3d0"
            : isDark ? "1px solid rgba(244,63,94,0.25)" : "1px solid #fecdd3",
          color: isAfter
            ? isDark ? "#34d399" : "#059669"
            : isDark ? "#fb7185" : "#e11d48",
          px: 1,
          py: 0.3,
          borderRadius: "6px",
          fontSize: "0.75rem",
          fontWeight: 600,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          maxWidth: "100%",
          wordBreak: "break-all",
        }}
      >
        {valStr}
      </Box>
    );
  };

  return (
    <Box display="flex" alignItems="flex-start" gap={2} mb={2.5} sx={{ position: "relative" }}>
      <Avatar
        sx={{
          width: 34, height: 34, bgcolor: iconBg, color: "#fff",
          fontSize: "0.75rem", fontWeight: 700, boxShadow: iconShadow,
          zIndex: 1, flexShrink: 0,
        }}
      >
        {icon}
      </Avatar>

      <Box flex={1} borderRadius="12px" sx={{
        bgcolor: isDark ? "#0d1d33" : "#fff",
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
        boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.25)" : "0 2px 10px rgba(0,0,0,0.04)",
        overflow: "hidden",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: isDark ? "rgba(56,189,248,0.3)" : "#cbd5e1",
        },
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" px={2} pt={1.5} pb={0.75}>
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: titleColor, display: "flex", alignItems: "center", gap: 0.75 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#cbd5e1" : "#475569", display: "block", mt: 0.25, fontWeight: 500 }}>
              {userName}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {relTime && (
              <Chip size="small" label={relTime}
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                  color: isDark ? "#94a3b8" : "#64748b",
                }}
              />
            )}
            {diffItems?.length > 0 && (
              <Button
                size="small"
                onClick={() => setOpen((p) => !p)}
                endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.74rem",
                  fontWeight: 600,
                  borderRadius: "20px",
                  py: 0.35,
                  px: 1.5,
                  bgcolor: open
                    ? (isDark ? "rgba(56,189,248,0.18)" : "#e0f2fe")
                    : (isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9"),
                  color: open
                    ? (isDark ? "#38bdf8" : "#0284c7")
                    : (isDark ? "#cbd5e1" : "#475569"),
                  border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(56,189,248,0.25)" : "#bae6fd",
                  },
                }}
              >
                {open ? "Hide Details" : isCreatedEvent ? `View ${diffItems.length} Fields` : `View ${diffItems.length} Changes`}
              </Button>
            )}
          </Box>
        </Box>

        {date && (
          <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8", display: "block", px: 2, pb: 1.25 }}>
            {formatAuditDate(date)}
          </Typography>
        )}

        {diffItems?.length > 0 && (
          <Collapse in={open}>
            <Box mx={2} mb={2} borderRadius="10px" sx={{
              bgcolor: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
              border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
              overflow: "hidden",
            }}>
              {/* Toolbar */}
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} px={2} py={1} sx={{
                bgcolor: isDark ? "rgba(56,189,248,0.06)" : "#eff6ff",
                borderBottom: isDark ? "1px solid rgba(56,189,248,0.15)" : "1px solid #dbeafe",
              }}>
                <Box display="flex" alignItems="center" gap={1}>
                  {isCreatedEvent ? (
                    <InitialIcon sx={{ fontSize: 16, color: "#10b981" }} />
                  ) : (
                    <CompareIcon sx={{ fontSize: 16, color: "#38bdf8" }} />
                  )}
                  <Typography variant="caption" fontWeight={700} sx={{ color: isCreatedEvent ? "#10b981" : "#0284c7", letterSpacing: 0.5 }}>
                    {isCreatedEvent ? `INITIAL FIELD SNAPSHOT (${diffItems.length})` : `${diffItems.length} FIELDS MODIFIED`}
                  </Typography>
                </Box>

                {/* Filters */}
                <Box display="flex" alignItems="center" gap={1}>
                  {isCreatedEvent && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip
                        size="small"
                        clickable
                        onClick={() => setShowOnlyConfigured(false)}
                        label={`All (${diffItems.length})`}
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          bgcolor: !showOnlyConfigured
                            ? (isDark ? "#0284c7" : "#0284c7")
                            : (isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"),
                          color: !showOnlyConfigured ? "#fff" : (isDark ? "#94a3b8" : "#64748b"),
                        }}
                      />
                      <Chip
                        size="small"
                        clickable
                        onClick={() => setShowOnlyConfigured(true)}
                        label={`Configured only (${configuredCount})`}
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          bgcolor: showOnlyConfigured
                            ? (isDark ? "#10b981" : "#10b981")
                            : (isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"),
                          color: showOnlyConfigured ? "#fff" : (isDark ? "#94a3b8" : "#64748b"),
                        }}
                      />
                    </Box>
                  )}

                  <TextField
                    size="small"
                    placeholder="Search field…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 14, color: isDark ? "#64748b" : "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 150,
                      "& .MuiInputBase-root": {
                        height: 26,
                        fontSize: "0.72rem",
                        bgcolor: isDark ? "#091526" : "#fff",
                        borderRadius: "6px",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Table */}
              {filteredItems.length === 0 ? (
                <Box p={2.5} textAlign="center">
                  <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                    No fields match your search.
                  </Typography>
                </Box>
              ) : isCreatedEvent ? (
                /* Clean 2-column layout for Record Initialized & Created: No red dashes! */
                <Table size="small" sx={{ "& td, & th": { py: 0.8, px: 2, fontSize: "0.75rem", borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", width: "38%", textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                        Field Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", width: "62%", textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                        Initial Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((d, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          bgcolor: i % 2 === 0 ? (isDark ? "transparent" : "#fff") : (isDark ? "rgba(255,255,255,0.015)" : "#fafafa"),
                          "&:hover": {
                            bgcolor: isDark ? "rgba(56,189,248,0.05)" : "#f0f9ff",
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: isDark ? "#e2e8f0" : "#1e293b", verticalAlign: "middle" }}>
                          {d.field}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "middle" }}>
                          {renderBadge(d.after, "after")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                /* 4-column layout for Modification diff */
                <Table size="small" sx={{ "& td, & th": { py: 0.8, px: 1.5, fontSize: "0.75rem", borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", width: "30%", textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                        Field
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", width: "33%", textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                        Previous Value
                      </TableCell>
                      <TableCell sx={{ width: "4%", textAlign: "center" }} />
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", width: "33%", textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                        Updated Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((d, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          bgcolor: i % 2 === 0 ? (isDark ? "transparent" : "#fff") : (isDark ? "rgba(255,255,255,0.015)" : "#fafafa"),
                          "&:hover": {
                            bgcolor: isDark ? "rgba(56,189,248,0.05)" : "#f0f9ff",
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: isDark ? "#e2e8f0" : "#1e293b", verticalAlign: "middle" }}>
                          {d.field}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "middle" }}>
                          {renderBadge(d.before, "before")}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", color: isDark ? "#38bdf8" : "#0284c7", fontWeight: 700 }}>
                          →
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "middle" }}>
                          {renderBadge(d.after, "after")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function AuditTrailDialog({
  open,
  onClose,
  title = "Audit Trail",
  entityType = "Record",
  data = null,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [usersMap, setUsersMap] = useState(cachedUsersMap || {});
  const [loadingUsers, setLoadingUsers] = useState(
    !(cachedUsersMap && Object.keys(cachedUsersMap).length > 0)
  );

  useEffect(() => {
    if (!open) return;
    if (cachedUsersMap && Object.keys(cachedUsersMap).length > 0) {
      setUsersMap(cachedUsersMap);
      setLoadingUsers(false);
      return;
    }
    if (isFetchingUsers) {
      const listener = (map) => {
        setUsersMap(map);
        setLoadingUsers(false);
      };
      userFetchListeners.push(listener);
      return () => {
        const idx = userFetchListeners.indexOf(listener);
        if (idx !== -1) userFetchListeners.splice(idx, 1);
      };
    }

    isFetchingUsers = true;
    setLoadingUsers(true);

    async function loadUsers() {
      // Seed initially from decodedToken in localStorage if available
      const initialMap = {};
      try {
        const dt = JSON.parse(localStorage.getItem("decodedToken") || "{}");
        const dtId = dt?.Id ?? dt?.id ?? dt?.UserId ?? dt?.userId;
        if (dtId != null) {
          const myName =
            [dt.FirstName ?? dt.firstName, dt.LastName ?? dt.lastName]
              .filter(Boolean)
              .join(" ") ||
            dt.Username ||
            dt.username ||
            dt.Name ||
            dt.name;
          if (myName) {
            const myInfo = { name: myName, role: dt.RoleName || dt.Role || "" };
            initialMap[dtId] = myInfo;
            initialMap[String(dtId).trim()] = myInfo;
            const num = Number(dtId);
            if (!isNaN(num)) initialMap[num] = myInfo;
          }
        }
      } catch (_) {}

      try {
        let list = [];
        const response = await fetchJsonDynamic("allUsersView", null, null, userManagementUrl.uri);
        if (response?.results) {
          list = typeof response.results === "string" ? JSON.parse(response.results) : response.results;
        } else if (Array.isArray(response)) {
          list = response;
        }

        // Fallback: If empty, try createUsersClient with GET_USERS query
        if (!Array.isArray(list) || list.length === 0) {
          try {
            const client = createUsersClient();
            const apolloRes = await client.query({ query: GET_USERS });
            if (apolloRes?.data?.allUsersView?.results) {
              const resStr = apolloRes.data.allUsersView.results;
              list = typeof resStr === "string" ? JSON.parse(resStr) : resStr;
            }
          } catch (apolloErr) {
            console.warn("Audit trail: Apollo fallback user query failed:", apolloErr);
          }
        }

        const map = { ...initialMap };
        if (Array.isArray(list)) {
          list.forEach((u) => {
            const id = u.Id ?? u.id ?? u.UserId ?? u.userId;
            const firstName = u.FirstName ?? u.firstName ?? "";
            const otherNames = u.OtherNames ?? u.otherNames ?? "";
            const lastName = u.LastName ?? u.lastName ?? "";
            const roleName = u.RoleName ?? u.roleName ?? u.Role ?? u.role ?? "";
            const username = u.Username ?? u.username ?? "";
            const directName = u.Name ?? u.name ?? "";
            const fullName = [firstName, otherNames, lastName].filter(Boolean).join(" ") || directName;
            if (id != null) {
              const info = {
                name: fullName || username || `User #${id}`,
                role: roleName,
              };
              map[id] = info;
              map[String(id)] = info;
              map[String(id).trim()] = info;
              const numId = Number(id);
              if (!isNaN(numId)) {
                map[numId] = info;
              }
            }
          });
        }

        if (Object.keys(map).length > 0) {
          cachedUsersMap = map;
        }
        setUsersMap(map);
        userFetchListeners.forEach((fn) => fn(map));
        userFetchListeners.length = 0;
      } catch (err) {
        console.warn("Audit trail: failed to load users:", err);
      } finally {
        isFetchingUsers = false;
        setLoadingUsers(false);
      }
    }
    loadUsers();
  }, [open]);

  if (!data) return null;

  const recordId = data.Id ?? data.id ?? "N/A";
  const recordName = data.Name ?? data.name ?? data.ChargeName ?? data.chargeName ?? `${entityType} #${recordId}`;

  const createdById = data.CreatedBy ?? data.createdBy;
  const createdDate = data.CreateDate ?? data.CreatedDate ?? data.createDate ?? data.createdDate;
  const updatedById = data.UpdatedBy ?? data.updatedBy;
  const updateDate = data.UpdateDate ?? data.UpdatedDate ?? data.updateDate ?? data.updatedDate;
  const deactivatedById = data.DeactevatedBy ?? data.DeactivatedBy ?? data.deactevatedBy ?? data.deactivatedBy;
  const deactivatedDate = data.DeactivatedDate ?? data.deactivatedDate;
  const isActive = data.IsActive === true || data.IsActive === 1 || data.IsActive === "True" || data.isActive === true;

  const resolveUser = (id) => {
    if (id == null || id === "") return "System";
    const key = String(id).trim();
    const u = usersMap[id] || usersMap[key] || usersMap[Number(key)];
    if (!u) return loadingUsers ? "Resolving…" : `User #${id}`;
    return u.role ? `${u.name} · ${u.role}` : u.name;
  };

  const resolveUserShort = (id) => {
    if (id == null || id === "") return "System";
    const key = String(id).trim();
    const u = usersMap[id] || usersMap[key] || usersMap[Number(key)];
    if (!u) return loadingUsers ? "…" : `User #${id}`;
    return u.name;
  };

  // Build field snapshot (current state of non-audit fields)
  const currentFields = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(data || {})) {
      if (!SKIP_FIELDS.has(k)) out[k] = v;
    }
    return out;
  }, [data]);

  // Creation diff: before = "—", after = current value
  const creationDiff = useMemo(() =>
    Object.entries(currentFields).map(([k, v]) => ({
      field: labelify(k),
      before: "—",
      after: v == null ? "—" : String(v),
    })),
    [currentFields]
  );

  // Retrieve any client-tracked historical audit events for this entity
  const storedAuditEvents = useMemo(() => {
    return getStoredAuditHistory(entityType, recordId);
  }, [entityType, recordId, open]);

  // Build timeline (capped at 20)
  const timelineEvents = useMemo(() => {
    const events = [];

    // 1. Specifically tracked audit events with real before/after values
    if (storedAuditEvents && storedAuditEvents.length > 0) {
      storedAuditEvents.forEach((item) => {
        events.push({
          type: item.type || "modified",
          date: item.date,
          userId: item.userId,
          diff: item.diff || [],
        });
      });
    } else {
      // 2. If no client-tracked events exist yet, derive from backend timestamps
      if (updateDate && updateDate !== createdDate) {
        const inferredDiff = [];
        if (!isActive) {
          inferredDiff.push({
            field: "Is Active",
            before: "Active / True",
            after: "Inactive / False",
          });
          inferredDiff.push({
            field: "Operational Status",
            before: "Operational",
            after: "Deactivated",
          });
        }

        events.push({
          type: !isActive ? "deactivated" : "modified",
          date: updateDate,
          userId: updatedById,
          diff: inferredDiff,
        });
      }
    }

    // 3. Initial Baseline Creation Event: Indicates ALL initial data
    events.push({
      type: "created",
      date: createdDate,
      userId: createdById,
      diff: creationDiff,
    });

    return events.slice(0, 20);
  }, [
    createdDate,
    createdById,
    updateDate,
    updatedById,
    isActive,
    creationDiff,
    storedAuditEvents,
  ]);

  const eventConfig = {
    created: {
      icon: <AddCircleIcon sx={{ fontSize: 18 }} />,
      iconBg: "#10b981", iconShadow: "0 0 10px rgba(16,185,129,0.4)",
      title: "Record Initialized & Created", titleColor: "#10b981",
      label: (uid) => `Created by ${resolveUser(uid)}`,
    },
    modified: {
      icon: <EditIcon sx={{ fontSize: 16 }} />,
      iconBg: "#0284c7", iconShadow: "0 0 10px rgba(2,132,199,0.4)",
      title: "Record Modified", titleColor: "#38bdf8",
      label: (uid) => `Modified by ${resolveUser(uid)}`,
    },
    deactivated: {
      icon: <CancelIcon sx={{ fontSize: 18 }} />,
      iconBg: "#f43f5e", iconShadow: "0 0 10px rgba(244,63,94,0.4)",
      title: "Record Deactivated", titleColor: "#fb7185",
      label: (uid) => `Deactivated by ${resolveUser(uid)}`,
    },
    active: {
      icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
      iconBg: "#6366f1", iconShadow: "0 0 10px rgba(99,102,241,0.4)",
      title: "Currently Active & Available", titleColor: "#818cf8",
      label: () => `${entityType} is enabled for operations`,
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: isDark ? "#0e1e33" : "#fff",
          color: isDark ? "#f8fafc" : "#0f172a",
          border: isDark ? "1px solid rgba(56,189,248,0.2)" : "1px solid #e2e8f0",
          boxShadow: isDark ? "0 25px 65px rgba(0,0,0,0.65)" : "0 20px 45px rgba(0,0,0,0.12)",
          backgroundImage: "none",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        py: 2, px: 3,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
        background: isDark
          ? "linear-gradient(135deg, #132a48 0%, #0d1f36 100%)"
          : "linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%)",
      }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: isDark ? "rgba(56,189,248,0.2)" : "rgba(2,132,199,0.15)", color: isDark ? "#38bdf8" : "#0284c7", width: 40, height: 40 }}>
            <HistoryIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
              Tracking audit lifecycle for {entityType.toLowerCase()}: <strong>{recordName}</strong>
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip size="small"
            icon={isActive ? <CheckCircleIcon /> : <CancelIcon />}
            label={isActive ? "Active" : "Deactivated"}
            color={isActive ? "success" : "default"}
            sx={{ fontWeight: 700, height: 24, fontSize: "0.72rem" }}
          />
          <IconButton size="small" onClick={onClose} sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3, bgcolor: isDark ? "#091526" : "#f8fafc" }}>
        {/* Resolving indicator */}
        {loadingUsers && (
          <Box display="flex" alignItems="center" gap={1} mb={2}
            sx={{ bgcolor: isDark ? "rgba(56,189,248,0.08)" : "#eff6ff", borderRadius: "8px", px: 2, py: 1 }}>
            <CircularProgress size={14} sx={{ color: "#38bdf8" }} />
            <Typography variant="caption" sx={{ color: isDark ? "#7dd3fc" : "#0284c7" }}>
              Resolving user names…
            </Typography>
          </Box>
        )}

        {/* KPI Cards */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(3, 1fr)" }} gap={2} mb={3}>
          {/* Created By Card */}
          <Box p={2.2} borderRadius="14px" sx={{
            background: isDark
              ? "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.03) 100%)"
              : "linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)",
            border: isDark ? "1px solid rgba(16,185,129,0.25)" : "1px solid #a7f3d0",
            boxShadow: isDark ? "0 4px 15px rgba(0,0,0,0.2)" : "0 2px 8px rgba(16,185,129,0.08)",
            transition: "transform 0.15s ease",
            "&:hover": { transform: "translateY(-1px)" },
          }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "8px",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: isDark ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.15)",
                color: "#10b981",
              }}>
                <AddCircleIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="caption" fontWeight={700} sx={{ color: "#10b981", letterSpacing: 0.6 }}>
                CREATED BY
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: isDark ? "#f1f5f9" : "#0f172a", fontSize: "0.9rem" }} noWrap>
              {resolveUserShort(createdById)}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mt: 0.5 }}>
              {formatAuditDate(createdDate)}
            </Typography>
          </Box>

          {/* Last Modified By Card */}
          <Box p={2.2} borderRadius="14px" sx={{
            background: isDark
              ? "linear-gradient(135deg, rgba(56,189,248,0.12) 0%, rgba(56,189,248,0.03) 100%)"
              : "linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)",
            border: isDark ? "1px solid rgba(56,189,248,0.25)" : "1px solid #bae6fd",
            boxShadow: isDark ? "0 4px 15px rgba(0,0,0,0.2)" : "0 2px 8px rgba(56,189,248,0.08)",
            transition: "transform 0.15s ease",
            "&:hover": { transform: "translateY(-1px)" },
          }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "8px",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: isDark ? "rgba(56,189,248,0.2)" : "rgba(56,189,248,0.15)",
                color: "#38bdf8",
              }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography variant="caption" fontWeight={700} sx={{ color: isDark ? "#38bdf8" : "#0284c7", letterSpacing: 0.6 }}>
                LAST MODIFIED BY
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: isDark ? "#f1f5f9" : "#0f172a", fontSize: "0.9rem" }} noWrap>
              {updateDate ? resolveUserShort(updatedById) : "Never Modified"}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mt: 0.5 }}>
              {updateDate ? formatAuditDate(updateDate) : "—"}
            </Typography>
          </Box>

          {/* Lifecycle Status Card */}
          <Box p={2.2} borderRadius="14px" sx={{
            background: isActive
              ? (isDark
                ? "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.03) 100%)"
                : "linear-gradient(135deg, #eef2ff 0%, #ffffff 100%)")
              : (isDark
                ? "linear-gradient(135deg, rgba(244,63,94,0.12) 0%, rgba(244,63,94,0.03) 100%)"
                : "linear-gradient(135deg, #fff1f2 0%, #ffffff 100%)"),
            border: isActive
              ? (isDark ? "1px solid rgba(99,102,241,0.25)" : "1px solid #c7d2fe")
              : (isDark ? "1px solid rgba(244,63,94,0.25)" : "1px solid #fecdd3"),
            boxShadow: isDark ? "0 4px 15px rgba(0,0,0,0.2)" : "0 2px 8px rgba(99,102,241,0.08)",
            transition: "transform 0.15s ease",
            "&:hover": { transform: "translateY(-1px)" },
          }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "8px",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: isActive
                  ? (isDark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.15)")
                  : (isDark ? "rgba(244,63,94,0.2)" : "rgba(244,63,94,0.15)"),
                color: isActive ? "#818cf8" : "#f43f5e",
              }}>
                <ShieldIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="caption" fontWeight={700} sx={{ color: isActive ? (isDark ? "#a5b4fc" : "#4f46e5") : "#f43f5e", letterSpacing: 0.6 }}>
                {isActive ? "STATUS: ACTIVE" : "DEACTIVATED BY"}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: isDark ? "#f1f5f9" : "#0f172a", fontSize: "0.9rem" }} noWrap>
              {isActive ? "Operational" : deactivatedById ? resolveUserShort(deactivatedById) : "Deactivated"}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mt: 0.5 }}>
              {deactivatedDate ? formatAuditDate(deactivatedDate) : "Currently Enabled for Operations"}
            </Typography>
          </Box>
        </Box>

        {/* Timeline header */}
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: isDark ? "#e2e8f0" : "#1e293b", letterSpacing: 0.5 }}>
            Change History
          </Typography>
          <Chip size="small"
            label={`${timelineEvents.length} event${timelineEvents.length !== 1 ? "s" : ""}`}
            sx={{ height: 18, fontSize: "0.65rem", bgcolor: isDark ? "rgba(56,189,248,0.12)" : "#dbeafe", color: isDark ? "#38bdf8" : "#1d4ed8" }}
          />
          <Typography variant="caption" sx={{ color: isDark ? "#475569" : "#94a3b8", ml: "auto" }}>
            Click ▾ on any event to see field changes
          </Typography>
        </Box>

        <Box sx={{
          position: "relative", pl: { xs: 2, sm: 3 },
          "&::before": {
            content: '""', position: "absolute",
            left: { xs: 23, sm: 31 }, top: 14, bottom: 14,
            width: 2, bgcolor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
          },
        }}>
          {timelineEvents.map((ev, idx) => {
            const cfg = eventConfig[ev.type];
            return (
              <TimelineItem
                key={idx}
                type={ev.type}
                icon={cfg.icon}
                iconBg={cfg.iconBg}
                iconShadow={cfg.iconShadow}
                title={cfg.title}
                titleColor={cfg.titleColor}
                date={ev.date}
                userName={cfg.label(ev.userId)}
                relTime={getRelativeTime(ev.date)}
                diffItems={ev.diff}
                isDark={isDark}
              />
            );
          })}
        </Box>

        {/* Technical Metadata */}
        <Box mt={2} p={2} borderRadius="10px" sx={{
          bgcolor: isDark ? "#0d1d33" : "#fff",
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
        }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <FingerprintIcon fontSize="small" sx={{ color: "#38bdf8" }} />
            <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: 0.5, color: isDark ? "#94a3b8" : "#64748b" }}>
              TECHNICAL AUDIT METADATA
            </Typography>
          </Box>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={0.75}
            sx={{ fontSize: "0.75rem", fontFamily: "ui-monospace, monospace", color: isDark ? "#cbd5e1" : "#334155" }}>
            <div><span style={{ color: "#38bdf8" }}>entity_type</span>: "{entityType}"</div>
            <div><span style={{ color: "#38bdf8" }}>record_id</span>: {recordId}</div>
            <div><span style={{ color: "#38bdf8" }}>created_by</span>: <strong style={{ color: isDark ? "#a5f3fc" : "#0284c7" }}>"{resolveUserShort(createdById)}"</strong></div>
            <div><span style={{ color: "#38bdf8" }}>create_date</span>: "{createdDate ?? "null"}"</div>
            <div><span style={{ color: "#38bdf8" }}>updated_by</span>: <strong style={{ color: isDark ? "#a5f3fc" : "#0284c7" }}>"{updatedById ? resolveUserShort(updatedById) : "null"}"</strong></div>
            <div><span style={{ color: "#38bdf8" }}>update_date</span>: "{updateDate ?? "null"}"</div>
            {deactivatedById != null && (
              <div><span style={{ color: "#fb7185" }}>deactivated_by</span>: <strong style={{ color: isDark ? "#fda4af" : "#e11d48" }}>"{resolveUserShort(deactivatedById)}"</strong></div>
            )}
            {deactivatedDate && (
              <div><span style={{ color: "#fb7185" }}>deactivated_date</span>: "{deactivatedDate}"</div>
            )}
            <div><span style={{ color: "#38bdf8" }}>is_active</span>: {String(isActive)}</div>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{
        px: 3, py: 1.5,
        borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
        bgcolor: isDark ? "#0e1e33" : "#fff",
        justifyContent: "space-between", alignItems: "center",
      }}>
        <Typography variant="caption" sx={{ color: isDark ? "#475569" : "#94a3b8" }}>
          Showing up to 20 most recent events
        </Typography>
        <Button onClick={onClose} variant="contained" size="small"
          sx={{ bgcolor: "#0284c7", color: "#fff", fontWeight: 700, textTransform: "none", px: 2.5, borderRadius: "8px", "&:hover": { bgcolor: "#0369a1" } }}>
          Close Audit Trail
        </Button>
      </DialogActions>
    </Dialog>
  );
}
